import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Divider,
  FormControl,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useState } from "react";
import { FaCheckCircle, FaStopCircle } from "react-icons/fa";
import { useParams } from "react-router-dom";
import {
  getTransferBadgeColor,
  GET_THIRD_PARTY_PAYOUT,
  REVIEW_THIRD_PARTY_PAYOUT,
} from "../../../../features/third-party-payouts/constants";
import { ThirdPartyIntegratorPayout } from "../../../../features/third-party-payouts/types";
import { formatAsNaira } from "../../../../helpers/helpers";
import { TransferStatus } from "../../../../types";
import Loading from "../../../../_components/Loading";
import RequestFailed from "../../../../_components/RequestFailed";

export default function Page() {
  const { id } = useParams<{
    id: string;
  }>();

  const [isOverriding, setIsOverriding] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState<string>();
  const [overrideReason, setOverrideReason] = useState<string>();

  const { data, loading, error } = useQuery<{
    thirdPartyIntegratorPayout: ThirdPartyIntegratorPayout | null;
  }>(GET_THIRD_PARTY_PAYOUT, {
    variables: {
      id,
    },
  });

  const isReviewable = !data?.thirdPartyIntegratorPayout?.transfer;

  const overriddenAmount =
    isOverriding && overrideAmount !== undefined
      ? parseFloat(overrideAmount || "0")
      : undefined;

  const [reviewPayout, reviewPayoutResponse] = useMutation(
    REVIEW_THIRD_PARTY_PAYOUT,
    {
      refetchQueries: [
        {
          query: GET_THIRD_PARTY_PAYOUT,
          variables: { id },
        },
      ],
    }
  );

  if (loading) return <Loading />;

  if (error) return <RequestFailed />;

  if (!data?.thirdPartyIntegratorPayout)
    return <RequestFailed errorMessage='Not Found' />;

  const {
    breakdown,
    override,
    thirdPartyIntegrator,
    orders,
    transfer,
    payoutPeriodStart,
    payoutPeriodEnd,
    createdAt,
  } = data.thirdPartyIntegratorPayout;

  const handleOverrideAmountChange = (valueString: string) => {
    setOverrideAmount(valueString);
  };

  const handleIsOverridingChange = () => {
    setIsOverriding((prev) => !prev);
  };

  const netAmount =
    overriddenAmount !== undefined ? overriddenAmount : breakdown.net;

  const isValidNetAmount = netAmount > 0;

  const handleSubmitReview = () => {
    reviewPayout({
      variables: {
        payoutId: id,
        ...(overriddenAmount !== undefined && {
          override: {
            amount: overriddenAmount,
            reason: overrideReason,
          },
        }),
      },
    });
  };

  const canSubmitReview =
    isValidNetAmount &&
    isReviewable &&
    (overriddenAmount !== undefined ? !!overrideReason : true);

  return (
    <div className='grid grid-cols-3 gap-4'>
      <div className='col-span-2'>
        <div className='flex flex-col gap-8 p-6 bg-white border'>
          <div className='mb-4'>
            <h2 className='text-primary-500 text-lg font-medium m-0'>
              Third Party Integrator Payout Request #{id.slice(0, 8)}
            </h2>
            <p className='m-0 text-primary-300 text-sm'>
              Shows a detailed view of a third party integrator payout request
            </p>
          </div>

          <div className='grid grid-cols-[1fr,2fr] gap-2 max-w-md'>
            <p className='m-0 text-primary-300 text-sm'>Integrator Name</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.name}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Client Contact</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.client.firstName}{" "}
              {thirdPartyIntegrator.client.lastName}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Client Email</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.client.email}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Payout Period</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {new Date(payoutPeriodStart).toDateString()} -{" "}
              {new Date(payoutPeriodEnd).toDateString()}
            </p>

            <p className='m-0 text-primary-300 text-sm'>Service Fee Share</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.policy.serviceFeeSharePercentage * 100}%
            </p>

            <p className='m-0 text-primary-300 text-sm'>Remittance Interval</p>
            <p className='m-0 text-primary-500 text-sm font-medium'>
              {thirdPartyIntegrator.policy.remittanceInterval}
            </p>
          </div>

          <Divider />

          <div>
            <h3 className='text-primary-500 text-md font-medium mb-3'>
              Orders Included ({orders.length})
            </h3>
            <TableContainer>
              <Table size='sm' className='border'>
                <Thead>
                  <Tr>
                    <Th className='border'>Order Code</Th>
                    <Th className='border'>Date</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orders.map((order) => (
                    <Tr key={order.id}>
                      <Td className='border'>{order.orderCode}</Td>
                      <Td className='border'>
                        {new Date(order.createdAt).toLocaleDateString()}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </div>
        </div>
      </div>

      <div>
        <div className='flex flex-col gap-6 p-6 bg-white border'>
          <div>
            <h2 className='text-primary-500 text-lg font-medium m-0'>
              Breakdown
            </h2>
          </div>
          <Divider />

          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Status</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  <Badge
                    colorScheme={getTransferBadgeColor(
                      transfer?.status || TransferStatus.PENDING
                    )}
                  >
                    {transfer?.status?.replace("_", " ") ||
                      TransferStatus.PENDING}
                  </Badge>
                </p>
              </div>

              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>
                  Gross Amount (NGN)
                </p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {formatAsNaira(breakdown.gross)}
                </p>
              </div>

              {!isReviewable && (
                <div className='flex justify-between'>
                  <p className='m-0 text-primary-300 text-sm'>Overridden</p>
                  {override ? (
                    <FaCheckCircle className='text-green-500' />
                  ) : (
                    <FaStopCircle className='text-red-500' />
                  )}
                </div>
              )}

              {override && (
                <div className='flex justify-between'>
                  <p className='m-0 text-primary-300 text-sm'>
                    Override Reason
                  </p>
                  <p className='m-0 text-primary-500 text-sm font-medium text-right max-w-[200px]'>
                    {override.reason}
                  </p>
                </div>
              )}
            </div>
          </div>

          <Divider />

          {isReviewable && (
            <>
              <div className='flex flex-col gap-4'>
                <div className='flex flex-col gap-6'>
                  <div className='flex flex-col gap-2'>
                    <div className='flex justify-end w-full gap-2'>
                      <p className='m-0 text-primary-500 text-sm'>
                        Override Amount?
                      </p>
                      <Switch
                        size='md'
                        isChecked={isOverriding}
                        onChange={handleIsOverridingChange}
                        isDisabled={!isReviewable}
                      />
                    </div>

                    {isOverriding && (
                      <FormControl>
                        <NumberInput
                          onChange={handleOverrideAmountChange}
                          value={overrideAmount}
                          min={0}
                          defaultValue={breakdown.net.toString()}
                          isDisabled={!isReviewable}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                    )}
                  </div>

                  {isOverriding && (
                    <Textarea
                      className='font-normal'
                      placeholder='Reason for override (required)'
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      isDisabled={!isReviewable}
                    />
                  )}
                </div>
              </div>
              <Divider />
            </>
          )}

          <div className='flex flex-col gap-4'>
            <div className='flex flex-col gap-2'>
              <div className='flex justify-between'>
                <p className='m-0 text-primary-300 text-sm'>Net Amount (NGN)</p>
                <p className='m-0 text-primary-500 text-sm font-medium'>
                  {formatAsNaira(netAmount)}
                </p>
              </div>
            </div>
          </div>

          <Divider />

          <div className='flex flex-col gap-2'>
            <Button
              colorScheme='green'
              rightIcon={<FaCheckCircle />}
              isDisabled={!canSubmitReview}
              onClick={handleSubmitReview}
              isLoading={reviewPayoutResponse.loading}
            >
              Submit Review
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
